import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FiCopy, FiRefreshCw, FiAlertCircle, FiX } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const StreamSettings = ({ stream, onUpdate }) => {
  const [streamTitle, setStreamTitle] = useState(stream?.title || '');
  const [streamGame, setStreamGame] = useState(stream?.game || '');
  const [streamTags, setStreamTags] = useState(stream?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setStreamTitle(stream?.title || '');
    setStreamGame(stream?.game || '');
    setStreamTags(stream?.tags || []);
  }, [stream]);

  const handleCopyStreamKey = () => {
    try {
      navigator.clipboard.writeText(stream.streamKey);
      setCopied(true);
      toast.success('Stream key copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying stream key:', err);
      toast.error('Failed to copy stream key');
    }
  };

  const handleRegenerateStreamKey = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const newStreamKey = generateStreamKey();
      const streamRef = doc(db, 'streams', stream.id);
      await updateDoc(streamRef, {
        streamKey: newStreamKey
      });
      onUpdate({ ...stream, streamKey: newStreamKey });
      toast.success('Stream key regenerated successfully');
    } catch (err) {
      console.error('Error regenerating stream key:', err);
      setError('Failed to regenerate stream key');
      toast.error('Failed to regenerate stream key');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (!tagInput.trim()) return;
    
    const newTag = tagInput.trim().toLowerCase();
    if (streamTags.length >= 10) {
      toast.error('Maximum 10 tags allowed');
      return;
    }
    if (!streamTags.includes(newTag)) {
      setStreamTags([...streamTags, newTag]);
      setTagInput('');
    } else {
      toast.error('Tag already exists');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setStreamTags(streamTags.filter(tag => tag !== tagToRemove));
  };

  const handleUpdateStream = async () => {
    if (!streamTitle.trim()) {
      setError('Please enter a stream title');
      toast.error('Please enter a stream title');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const streamRef = doc(db, 'streams', stream.id);
      const updatedStream = {
        title: streamTitle.trim(),
        game: streamGame.trim(),
        tags: streamTags,
        updatedAt: new Date()
      };
      
      await updateDoc(streamRef, updatedStream);
      onUpdate({
        ...stream,
        ...updatedStream
      });
      
      toast.success('Stream settings updated successfully');
    } catch (err) {
      console.error('Error updating stream:', err);
      setError('Failed to update stream settings');
      toast.error('Failed to update stream settings');
    } finally {
      setIsLoading(false);
    }
  };

  const generateStreamKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let key = '';
    for (let i = 0; i < 32; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return key;
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <h2 className="text-xl font-bold text-white mb-4">Stream Settings</h2>
      
      {error && (
        <div className="bg-red-500/10 text-red-500 p-3 rounded-lg mb-4 flex items-center">
          <FiAlertCircle className="w-5 h-5 mr-2" />
          <span>{error}</span>
        </div>
      )}
      
      <div className="space-y-4">
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            Stream Key
          </label>
          <div className="flex">
            <input
              type="password"
              value={stream.streamKey}
              readOnly
              className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-l-md focus:outline-none"
            />
            <button
              onClick={handleCopyStreamKey}
              disabled={isLoading}
              className="bg-gray-700 text-white px-3 py-2 hover:bg-gray-600 disabled:opacity-50"
            >
              <FiCopy className="w-5 h-5" />
            </button>
            <button
              onClick={handleRegenerateStreamKey}
              disabled={isLoading}
              className="bg-gray-700 text-white px-3 py-2 rounded-r-md hover:bg-gray-600 disabled:opacity-50"
            >
              <FiRefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {copied && (
            <p className="text-green-400 text-sm mt-1">Copied to clipboard!</p>
          )}
        </div>
        
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            Stream Title
          </label>
          <input
            type="text"
            value={streamTitle}
            onChange={(e) => setStreamTitle(e.target.value)}
            placeholder="Enter stream title"
            maxLength={100}
            className="w-full bg-gray-700 text-white px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <p className="text-gray-400 text-sm mt-1">
            {streamTitle.length}/100 characters
          </p>
        </div>
        
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            Game
          </label>
          <input
            type="text"
            value={streamGame}
            onChange={(e) => setStreamGame(e.target.value)}
            placeholder="Enter game name"
            className="w-full bg-gray-700 text-white px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">
            Tags ({streamTags.length}/10)
          </label>
          <form onSubmit={handleAddTag} className="flex mb-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Add a tag"
              maxLength={20}
              className="flex-1 bg-gray-700 text-white px-3 py-2 rounded-l-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              type="submit"
              disabled={!tagInput.trim() || isLoading}
              className="bg-purple-600 text-white px-4 py-2 rounded-r-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </form>
          <div className="flex flex-wrap gap-2">
            {streamTags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-sm flex items-center"
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  disabled={isLoading}
                  className="ml-2 text-gray-400 hover:text-white disabled:opacity-50"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>
        </div>
        
        <button
          onClick={handleUpdateStream}
          disabled={isLoading}
          className="w-full bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Updating...' : 'Update Stream Info'}
        </button>
      </div>
    </div>
  );
};

export default StreamSettings; 